import { Suspense, forwardRef, useEffect, useMemo, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { ErrorBoundary } from "react-error-boundary";
import { Color, Mesh, MeshStandardMaterial, Vector3 } from "three";
import { useModel } from "hooks/useModel";
import { AppliableColor, AppliableMaterial } from "types/configurator";
import { ConfiguratorOptions, useCameraPosition } from "@rromikas/configurio";
import Rotator from "./rotator";

interface ModelProps {
  url: string;
  onLoad?: (model: ReturnType<typeof useModel>) => void;
  highlightIndex?: number;
  onSelect?: (index: number | undefined) => void;
  materials?: AppliableMaterial[];
  colors?: AppliableColor[];
  disableHover?: boolean;
  onCameraPosition?: (val: Vector3) => void;
  onTarget?: (val: Vector3) => void;
  initialCameraPosition: ConfiguratorOptions["initialCameraPosition"];
  initialTarget: ConfiguratorOptions["initialTarget"];
  rotation: ConfiguratorOptions["rotation"];
}

const Model = forwardRef<HTMLCanvasElement, ModelProps>(
  (
    {
      url,
      onLoad,
      onSelect,
      materials,
      colors,
      highlightIndex,
      disableHover = false,
      onCameraPosition,
      onTarget,
      initialCameraPosition,
      initialTarget,
      rotation,
    },
    ref
  ) => {
    const model = useModel({ url });
    const useCameraOptions = useMemo(
      () => ({ ...model, initialCameraPosition, initialTarget, fillPercentage: 70 }),
      [model, initialCameraPosition, initialTarget]
    );
    const { cameraPosition, cameraTarget: target, containerRef } = useCameraPosition(useCameraOptions);
    const [localHighlightIndex, setLocalHighlightIndex] = useState<number>();

    useEffect(() => {
      if (!materials) return;
      materials.forEach((material) => {
        const targetNode = Object.values(model.nodes)[material.targetIndex];
        const clonedNode = Object.values(model.clonedNodes)[material.targetIndex];
        const newMaterial = material.value.clone();
        newMaterial.color = (targetNode.material as MeshStandardMaterial).color;
        targetNode.material = newMaterial;
        clonedNode.material = newMaterial;
      });
    }, [model.scene, materials]);

    useEffect(() => {
      if (!colors) return;
      colors.forEach((color) => {
        const node = Object.values(model.nodes)[color.targetIndex];
        const clonedNode = Object.values(model.clonedNodes)[color.targetIndex];
        if (node && node.type === "Mesh") {
          let coloredMaterial = (node.material as MeshStandardMaterial).clone();
          coloredMaterial.color = new Color(color.value);
          node.material = coloredMaterial;
          clonedNode.material = coloredMaterial;
        }
      });
    }, [model, colors]);

    useEffect(() => {
      if (onLoad) {
        onLoad(model);
      }
    }, [model]);

    useEffect(() => {
      Object.values(model.nodes).map((node, i) => {
        if (node.type === "Mesh") {
          const meshObj = node as Mesh;
          if (highlightIndex === i || localHighlightIndex === i) {
            let newMaterial = (meshObj.material as MeshStandardMaterial).clone();
            newMaterial.emissive = new Color("rgb(100,100,100)");
            meshObj.material = newMaterial;
          } else {
            meshObj.material = model.clonedNodes[node.id].material;
          }
        }
      });
    }, [highlightIndex, model, localHighlightIndex]);

    const getIndexById = (id: number) => {
      const index = Object.values(model.nodes).findIndex((n) => n.id === id);
      return index === -1 ? undefined : index;
    };

    return (
      <div ref={containerRef as any} className="w-full h-full">
        <ErrorBoundary fallback={null}>
          <Suspense>
            <Canvas gl={{ preserveDrawingBuffer: true }} ref={ref}>
              <ambientLight intensity={1} />
              <directionalLight intensity={0.5} position={[1, 1, 1]} />
              <directionalLight intensity={0.5} position={[-1, -1, -1]} />
              <PerspectiveCamera
                near={0.01}
                onUpdate={(e) => {
                  if (onCameraPosition) {
                    onCameraPosition(e.position);
                  }
                }}
                makeDefault
                position={cameraPosition}
                rotation={[0.2609723234296186, 0.8206240136184555, -0.19294510479059357, "XYZ"]}
              ></PerspectiveCamera>
              <Rotator rotation={rotation}>
                <group
                  onPointerMissed={() => {
                    if (onSelect) {
                      onSelect(undefined);
                    }
                  }}
                  onClick={(e) => {
                    if (onSelect) {
                      const index = getIndexById(e.intersections[0].object.id);
                      onSelect(index);
                    }
                  }}
                  onPointerLeave={() => {
                    if (disableHover) return;
                    setLocalHighlightIndex(undefined);
                  }}
                  onPointerMove={(e) => {
                    if (disableHover) return;
                    setLocalHighlightIndex(getIndexById(e.intersections[0].object.id));
                  }}
                >
                  <primitive object={model.scene}></primitive>
                </group>
              </Rotator>
              <OrbitControls
                onChange={(e) => {
                  const target = e?.target.target;
                  if (target && onTarget) {
                    onTarget(target as Vector3);
                  }
                }}
                target={target}
              ></OrbitControls>
            </Canvas>
          </Suspense>
        </ErrorBoundary>
      </div>
    );
  }
);

export default Model;
